import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ActionRow from "../components/ActionRow";
import CurrencyPicker from "../components/CurrencyPicker";
import ExpenseRow from "../components/ExpenseRow";
import PendingSection from "../components/PendingSection";
import { useToast } from "../contexts/ToastContext";
import { api, ApiError, patchChatCurrency } from "../lib/api";
import { currencySymbol, formatNumber } from "../lib/format";
import { haptics } from "../lib/haptics";
import { useBackButton, useMainButton } from "../lib/telegram";
import { BalancesOut, ExpenseOut, MeOut, TransferOut } from "../lib/schemas";
import { Avatar, BalanceAmount, Skeleton } from "../ui";
import { ChevronRight } from "lucide-react";

interface GroupData {
  groupId: number;
  groupTitle: string;
  personalBalance: string;
  currency: string;
  recentExpenses: ExpenseOut[];
  currentTgId: number;
  plan: TransferOut[];
}

function useGroupData(groupId: number): {
  data: GroupData | undefined;
  error: string | null;
  loading: boolean;
  refetch: () => void;
} {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["group", groupId],
    queryFn: async () => {
      const [balances, history, me] = await Promise.all([
        api<BalancesOut>("GET", `/chats/${groupId}/balances`),
        api<ExpenseOut[]>("GET", `/chats/${groupId}/history?limit=5`),
        api<MeOut>("GET", "/me"),
      ]);
      const myGroup = me.groups.find((g) => g.id === groupId);
      const myRow = balances.rows.find((r) => r.user.telegram_id === me.user.telegram_id);
      const personalBalance = myRow?.net ?? myGroup?.personal_balance ?? "0";
      return {
        groupId,
        groupTitle: myGroup?.title ?? "",
        personalBalance,
        currency: balances.currency,
        recentExpenses: history,
        currentTgId: me.user.telegram_id,
        plan: balances.plan,
      } satisfies GroupData;
    },
    enabled: !isNaN(groupId),
  });

  return {
    data,
    error: error ? (error instanceof Error ? error.message : "Failed to load group") : null,
    loading: isLoading,
    refetch: () => { void refetch(); },
  };
}

export default function Group() {
  const nav = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const groupId = id ? Number(id) : NaN;
  const queryClient = useQueryClient();
  const { data, error, loading } = useGroupData(groupId);
  const showToast = useToast();

  const [undoLoading, setUndoLoading] = useState(false);
  const [undoPending, setUndoPending] = useState(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleBack = useCallback(() => nav("/"), [nav]);
  useBackButton(handleBack);

  const handleAddExpense = useCallback(() => {
    if (!isNaN(groupId)) nav(`/groups/${groupId}/expenses/new`);
  }, [nav, groupId]);
  useMainButton("+ Add Expense", !isNaN(groupId), handleAddExpense);

  const executeUndo = useCallback(async () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoPending(false);
    if (isNaN(groupId)) return;
    setUndoLoading(true);
    try {
      await api("POST", `/chats/${groupId}/undo`);
      showToast("Last expense undone", { type: "success" });
      void queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Undo failed";
      showToast(msg, { type: "error" });
    } finally {
      setUndoLoading(false);
    }
  }, [groupId, showToast, queryClient]);

  const cancelUndo = useCallback(() => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoPending(false);
  }, []);

  const handleUndo = useCallback(() => {
    if (isNaN(groupId) || undoLoading || undoPending) return;
    setUndoPending(true);
    undoTimerRef.current = setTimeout(() => void executeUndo(), 5000);
  }, [groupId, undoLoading, undoPending, executeUndo]);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  const handleCurrencyChange = useCallback(async (currency: string) => {
    if (isNaN(groupId)) return;
    await patchChatCurrency(groupId, currency);
    void queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    setPickerOpen(false);
  }, [groupId, queryClient]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <div className="bg-surface border border-hairline rounded-2xl shadow-card p-5 mb-4 text-center">
          <Skeleton className="h-3 w-24 mx-auto mb-3" />
          <Skeleton className="h-11 w-40 mx-auto" />
        </div>
        <div className="mb-6">
          <Skeleton className="h-16 w-full" rounded="rounded-xl" />
        </div>
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-4 py-3.5 flex items-center gap-3 border-t border-hairline first:border-t-0">
              <Skeleton className="w-10 h-10" rounded="rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-danger mb-4">{error}</p>
          <button
            type="button"
            onClick={() => nav("/")}
            className="border border-fg px-5 py-2.5 rounded-xl text-fg font-sans text-sm font-semibold active:opacity-70 transition-opacity"
          >
            Go home
          </button>
        </div>
      </div>
    );
  }

  if (!data || isNaN(groupId)) return null;

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-24">
      {/* Header */}
      {data.groupTitle && (
        <div className="flex items-center justify-center gap-2 mb-4">
          <h1 className="text-xl font-bold text-fg">
            {data.groupTitle}
          </h1>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="font-sans text-xs font-medium text-muted border border-hairline rounded-lg px-3.5 py-2.5 active:bg-surface transition-colors"
          >
            {data.currency} {currencySymbol(data.currency)}
          </button>
        </div>
      )}

      {/* Personal balance card */}
      {(() => {
        const sym = currencySymbol(data.currency);
        const relevantTransfers = data.plan.filter(
          t => t.from_user.telegram_id === data.currentTgId || t.to_user.telegram_id === data.currentTgId
        );
        return (
          <div className="w-full bg-surface border border-hairline rounded-2xl shadow-elevated p-5 mb-4">
            <div className="flex flex-col items-center py-2">
              <BalanceAmount size="hero" balance={data.personalBalance} currency={data.currency} />
              <p className="font-sans text-[13px] font-medium text-muted mt-2">
                Net balance
              </p>
            </div>

            {relevantTransfers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-hairline space-y-0.5">
                {relevantTransfers.map((t, i) => {
                  const isOwing = t.from_user.telegram_id === data.currentTgId;
                  const counterparty = isOwing ? t.to_user : t.from_user;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { haptics.tap(); nav(`/groups/${groupId}/settle`); }}
                      className="w-full flex items-center gap-3 py-1.5 active:opacity-70 transition-opacity"
                    >
                      <Avatar name={counterparty.first_name} size="sm" />
                      <span className="text-fg text-sm flex-1 text-left">{counterparty.first_name}</span>
                      <span className={`font-mono [font-variant-numeric:tabular-nums] text-sm font-medium ${isOwing ? "text-danger" : "text-success"}`}>
                        {isOwing ? "−" : "+"}{sym}{formatNumber(t.amount)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      <div className="mb-6">
        <ActionRow
          onReceipt={() => nav(`/groups/${groupId}/receipt`)}
          onSettle={() => nav(`/groups/${groupId}/settle`)}
          onUndo={undoLoading ? () => {} : handleUndo}
        />
      </div>

      <PendingSection
        groupId={groupId}
        currentTgId={data.currentTgId}
      />

      {/* Recent expenses section header */}
      <div className="mb-3 flex items-end justify-between">
        <h2 className="font-sans text-xs font-medium text-muted">
          Recent expenses
        </h2>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => nav(`/groups/${groupId}/balances`)}
            className="flex items-center gap-0.5 font-sans text-xs font-medium text-fg active:opacity-70 transition-opacity"
          >
            Balances <ChevronRight size={14} />
          </button>
          <button
            type="button"
            onClick={() => nav(`/groups/${groupId}/history`)}
            className="flex items-center gap-0.5 font-sans text-xs font-medium text-fg active:opacity-70 transition-opacity"
          >
            See all <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {data.recentExpenses.length === 0 ? (
        <p className="text-muted text-center py-6">No expenses yet.</p>
      ) : (
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {data.recentExpenses.map((expense) => (
            <ExpenseRow
              key={expense.id}
              expense={expense}
              currency={data.currency}
              onClick={() => nav(`/groups/${groupId}/expenses/${expense.id}`, { state: { expense } })}
            />
          ))}
        </div>
      )}

      {pickerOpen && (
        <CurrencyPicker
          currentCurrency={data.currency}
          onSelect={handleCurrencyChange}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* Undo snackbar */}
      {undoPending && (
        <div className="fixed bottom-20 left-4 right-4 z-50 bg-fg text-bg rounded-xl shadow-elevated px-4 py-3 flex items-center gap-3">
          <span className="flex-1 text-sm">Undo last expense?</span>
          <button
            type="button"
            onClick={cancelUndo}
            className="font-sans text-xs font-medium px-3 py-1.5 rounded-lg border border-bg/20 active:opacity-70 transition-opacity"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void executeUndo()}
            className="font-sans text-xs font-semibold px-1 active:opacity-70 transition-opacity"
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}
